import { useCallback, useEffect, useMemo, useState } from 'react';

import { Link } from 'react-router-dom';

import { ApiSpecView } from './ApiSpecView';
import { Empty, EntityChip, Modal } from './ui';
import { api } from '../lib/api';

interface Param { name: string; in: string; type: string; required: boolean }
interface Field { name: string; type: string; required: boolean }

interface SeenOn {
  deployment: string;
  deploymentId: string;
  client: string;
  server: string;
  serverId: string;
  hasSpec: boolean;
}

interface Operation {
  method: string;
  path: string;
  operationId: string | null;
  summary: string | null;
  tag: string | null;
  deprecated: boolean;
  isPresent: boolean;
  parameters: Param[];
  requestFields: Field[];
  requestSchema: string | null;
  signature: string;
  hasVariants: boolean;
  specTitle: string | null;
  /** Measured: any public endpoint reaches a deployment hosting this operation. */
  reachable: boolean;
  /** The public addresses. Can be empty while `reachable` — a raw port has no hostname. */
  publicUrls: string[];
  /** False means reachability was never measured — not "nothing is exposed". */
  proxyKnown: boolean;
  seenOn: SeenOn[];
}

/**
 * Public, private, or not yet known — derived, never typed in.
 *
 * Three states and not two, because the difference matters here more than
 * anywhere else in the app. An operation whose hosting deployment has no
 * endpoint rows at all has not been measured, and drawing a padlock on it would
 * assert something nobody checked. This is the one view that must never say
 * "private" about something answering on the internet, and the honest third
 * answer is what keeps that true while collection catches up.
 */
type Reach = 'public' | 'private' | 'unknown';

function reachOf(op: Operation): Reach {
  if (op.reachable) return 'public';
  // No padlock without a proxy config having been read. On a host where only
  // docker port bindings were collected, "nothing fronts this" is a guess, and
  // the reassuring guess is the one this view must never make.
  return op.proxyKnown ? 'private' : 'unknown';
}

interface VersionApi {
  version: string;
  operationCount: number;
  operations: Operation[];
}

interface ProductApiData {
  versions: VersionApi[];
  reachable: number;
  unknownReach: number;
}

/**
 * The handler name behind an operation, without FastAPI's generated suffix.
 *
 * FastAPI builds `operationId` as `{function}{path}_{method}`, with every
 * non-word character of the path replaced by an underscore. So the handler
 * `list_conversations` on `GET /api/conversations` is published as
 * `list_conversations_api_conversations_get`. In a table that already has the
 * method and the path in their own columns, that suffix is pure noise.
 *
 * The suffix is RECONSTRUCTED from this operation's own path and method and
 * stripped only on an exact match, rather than guessed at with a pattern. That
 * is what keeps other generators intact: PlatformIQ's own NestJS spec publishes
 * `InventoryController_servers`, which matches nothing here and is returned
 * untouched. Measured across the estate, this strips all 46 FastAPI ids and
 * leaves all 47 NestJS ones alone.
 *
 * The length guard stops an operationId that is nothing but a suffix from
 * collapsing to an empty label.
 */
function handlerName(op: Operation): string | null {
  if (!op.operationId) return null;
  const suffix = `${op.path.replace(/\W/g, '_')}_${op.method.toLowerCase()}`;
  return op.operationId.endsWith(suffix) && op.operationId.length > suffix.length
    ? op.operationId.slice(0, -suffix.length)
    : op.operationId;
}

/** `(question: string, chat_guid?: string)` — what you pass, in one line. */
function signatureText(op: Operation): string {
  const parts = [
    ...op.parameters.map((p) => `${p.name}${p.required ? '' : '?'}: ${p.type}`),
    ...op.requestFields.map((f) => `${f.name}${f.required ? '' : '?'}: ${f.type}`),
  ];
  return parts.length ? `(${parts.join(', ')})` : '()';
}

/**
 * The padlock in the list.
 *
 * A lock is shown for private and nothing at all for public, because in this
 * estate nearly everything is behind Caddy and public — so the padlock marks
 * the exception and the list stays readable. The lock carries a text label
 * beside it rather than standing alone: an icon-only state fails for anyone
 * using a screen reader and for anyone who does not already know the
 * convention, which is the same rule the server status pills follow.
 *
 * "Unknown" is drawn, not hidden. Silence would be indistinguishable from
 * public, and guessing in favour of the reassuring answer is the one mistake
 * this view cannot make.
 */
function ReachTag({ op }: { op: Operation }) {
  const reach = reachOf(op);
  if (reach === 'public') return null;
  if (reach === 'unknown') {
    return (
      <span
        className="tag"
        title="No reverse-proxy config has been read for this host, so nothing public was found but nothing was ruled out either. Not a claim that this endpoint is private."
      >
        reach unknown
      </span>
    );
  }
  return (
    <span
      className="tag tag-private"
      title="No Caddy site, published port or tunnel reaches the hosting deployment from outside"
    >
      🔒 private
    </span>
  );
}

type RowStatus = 'same' | 'changed' | 'partial';

interface Row {
  key: string;
  method: string;
  path: string;
  name: string;
  /** The spec's own operationId, kept for the tooltip so the shortened label
   *  can always be traced back to what the document actually said. */
  rawOperationId: string | null;
  byVersion: Map<string, Operation>;
  status: RowStatus;
}

/**
 * Every operation of a product against every version that serves it.
 *
 * A matrix rather than a tab per version, because the question is comparative:
 * what did this release add, drop or change. Tabs put the two things being
 * compared on separate screens and make the reader hold one in their head.
 *
 * Rows that differ are sorted to the top. An API that is stable across versions
 * is the boring, expected case and does not need to be read.
 */
export function ProductApi({ productKey }: { productKey: string }) {
  const [data, setData] = useState<ProductApiData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [onlyDiffs, setOnlyDiffs] = useState(false);
  const [onlyPublic, setOnlyPublic] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  /** The operation whose spec is open, and which deployment's copy to read. */
  const [spec, setSpec] = useState<{
    deploymentId: string;
    method: string;
    path: string;
    label: string;
  } | null>(null);

  const load = useCallback(() => {
    api<ProductApiData>(`/products/${encodeURIComponent(productKey)}/api`)
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not load the API'));
  }, [productKey]);

  useEffect(load, [load]);

  const { versions, rows, diffCount } = useMemo(() => {
    const versions = data?.versions.map((v) => v.version) ?? [];
    const byKey = new Map<string, Row>();

    for (const v of data?.versions ?? []) {
      for (const op of v.operations) {
        const key = `${op.method} ${op.path}`;
        const row = byKey.get(key) ?? {
          key,
          method: op.method,
          path: op.path,
          name: handlerName(op) ?? op.summary ?? op.path,
          rawOperationId: op.operationId,
          byVersion: new Map<string, Operation>(),
          status: 'same' as RowStatus,
        };
        row.byVersion.set(v.version, op);
        byKey.set(key, row);
      }
    }

    for (const row of byKey.values()) {
      // Absent from at least one version — added or removed between releases.
      if (row.byVersion.size < versions.length) row.status = 'partial';
      else {
        const sigs = new Set([...row.byVersion.values()].map((o) => o.signature));
        // Same endpoint everywhere, different inputs: the breaking change that a
        // version number alone would never reveal.
        if (sigs.size > 1) row.status = 'changed';
      }
    }

    const order: Record<RowStatus, number> = { changed: 0, partial: 1, same: 2 };
    const rows = [...byKey.values()].sort(
      (a, b) => order[a.status] - order[b.status] || a.path.localeCompare(b.path) || a.method.localeCompare(b.method),
    );
    return { versions, rows, diffCount: rows.filter((r) => r.status !== 'same').length };
  }, [data]);

  if (error) return <p className="form-error">{error}</p>;
  if (!data) return <p className="muted small">Loading API…</p>;
  if (versions.length === 0) {
    return (
      <Empty>
        No API discovered. PlatformIQ reads each container's OpenAPI spec — a service that does
        not serve one has nothing to show here.
      </Empty>
    );
  }

  /** Any version's copy: reachability is a property of the hosting deployment. */
  const anyOp = (row: Row): Operation => [...row.byVersion.values()][0];

  let shown = onlyDiffs ? rows.filter((r) => r.status !== 'same') : rows;
  if (onlyPublic) shown = shown.filter((r) => reachOf(anyOp(r)) === 'public');

  return (
    <div className="api-versions">
      <div className="api-legend">
        <span className="muted small">
          {rows.length} operation{rows.length === 1 ? '' : 's'} across {versions.length} version
          {versions.length === 1 ? '' : 's'}
          {diffCount > 0 && <> · <strong>{diffCount} differ</strong></>}
          {' · '}
          {data.reachable} reachable from the internet
          {data.unknownReach > 0 && <> · {data.unknownReach} not yet measured</>}
        </span>
        {diffCount > 0 && (
          <label className="check">
            <input type="checkbox" checked={onlyDiffs} onChange={(e) => setOnlyDiffs(e.target.checked)} />
            Only differences
          </label>
        )}
        {data.reachable > 0 && (
          <label className="check">
            <input type="checkbox" checked={onlyPublic} onChange={(e) => setOnlyPublic(e.target.checked)} />
            Only publicly reachable
          </label>
        )}
      </div>

      <div className="table-scroll">
        <table className="data-table api-matrix">
          <thead>
            <tr>
              <th scope="col">Operation</th>
              <th scope="col">Endpoint</th>
              {versions.map((v) => (
                <th key={v} scope="col" className="ver-col">{v}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((row) => {
              const open = expanded === row.key;
              return (
                <tr key={row.key} className={open ? 'row-open' : undefined}>
                  <td>
                    <button
                      type="button"
                      className="linklike"
                      aria-expanded={open}
                      // The full operationId on hover. The label is shortened
                      // for reading; the document's own value stays one hover
                      // away so a surprising name can be checked rather than
                      // argued about.
                      title={row.rawOperationId ?? undefined}
                      onClick={() => setExpanded(open ? null : row.key)}
                    >
                      {row.name}
                    </button>
                    {row.status === 'changed' && <span className="tag tag-warn">signature differs</span>}
                    {row.status === 'partial' && <span className="tag">not in all versions</span>}
                    {[...row.byVersion.values()].some((o) => o.hasVariants) && (
                      <span className="tag tag-warn" title="Two deployments of this version serve different shapes">
                        differs between hosts
                      </span>
                    )}
                    <ReachTag op={anyOp(row)} />
                    {open && (
                      <div className="api-detail">
                        {versions.map((v) => {
                          const op = row.byVersion.get(v);
                          // Versions that do not serve this operation are left
                          // out entirely. The matrix columns already say "—" for
                          // those; repeating it here is noise in a panel whose
                          // job is "where is this, and show me the spec".
                          if (!op) return null;
                          // The first deployment that actually has a stored
                          // document. A version on three hosts needs one spec
                          // link, not three identical ones.
                          const withSpec = op.seenOn.find((s) => s.hasSpec);
                          return (
                            <div key={v} className="api-detail-row">
                              <div className="api-detail-head">
                                <span className="ver">{v}</span>
                                {withSpec ? (
                                  <button
                                    type="button"
                                    className="linklike"
                                    onClick={() =>
                                      setSpec({
                                        deploymentId: withSpec.deploymentId,
                                        method: row.method,
                                        path: row.path,
                                        label: `${row.method} ${row.path} · ${v}`,
                                      })
                                    }
                                  >
                                    View spec
                                  </button>
                                ) : (
                                  <span className="muted small">no stored document — run API discovery</span>
                                )}
                              </div>
                              <table className="data-table api-where">
                                <thead>
                                  <tr>
                                    <th scope="col">Client</th>
                                    <th scope="col">Server</th>
                                    <th scope="col">Stack</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {op.seenOn.map((s) => (
                                    <tr key={s.deploymentId}>
                                      <td>
                                        {/* COMMON is a real answer, not a blank:
                                            a `-common` stack genuinely has no
                                            client. Rendered as a plain tag
                                            rather than a client chip, because a
                                            chip invites clicking through to a
                                            client that does not exist. */}
                                        {s.client === 'COMMON' ? (
                                          <span className="tag tag-common">COMMON</span>
                                        ) : (
                                          // Not a link: seenOn carries the client
                                          // code, not its id, and a chip that
                                          // navigated by code would 404.
                                          <EntityChip type="client" label={s.client} />
                                        )}
                                      </td>
                                      <td>
                                        <Link to={`/servers/${s.serverId}`}>{s.server}</Link>
                                      </td>
                                      <td>
                                        <code className="stack-name">{s.deployment}</code>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </td>
                  <td className="mono small">
                    <span className={`method method-${row.method.toLowerCase()}`}>{row.method}</span>{' '}
                    {row.path}
                  </td>
                  {versions.map((v) => {
                    const op = row.byVersion.get(v);
                    if (!op) return <td key={v} className="cell-no" title="Not in this version">—</td>;
                    if (row.status === 'changed') {
                      return (
                        <td key={v} className="cell-diff" title={signatureText(op)}>
                          {/* The signature is in the cell title and the expanded
                              detail; the glyph only says "this one is not the
                              same as the others". */}
                          ≠
                        </td>
                      );
                    }
                    return <td key={v} className="cell-yes" title={signatureText(op)}>✓</td>;
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="muted small">
        Read from each container's own OpenAPI document — what the running service exposes, not
        what a document says it should. <strong>✓</strong> present and identical,{' '}
        <strong>≠</strong> present with different parameters, <strong>—</strong> absent.
      </p>

      <Modal open={spec !== null} onClose={() => setSpec(null)} title={spec?.label ?? 'Spec'} wide>
        {spec && (
          <ApiSpecView
            deploymentId={spec.deploymentId}
            focus={{ method: spec.method, path: spec.path }}
          />
        )}
      </Modal>
    </div>
  );
}
